import { existsSync, promises as fs } from 'fs'
import path from 'path'
import { getConfig } from '@/src/utils/get-config'
import { getPackageManager } from '@/src/utils/get-package-manager'
import { handleError } from '@/src/utils/handle-error'
import { logger } from '@/src/utils/logger'
import {
  fetchTree,
  getItemTargetPath,
  getRegistryBaseColor,
  getRegistryIndex,
  resolveTree,
} from '@/src/utils/registry'
import {
  downloadImageFromRegistry,
  getComponentsFromExternalRegistry,
} from '@/src/utils/registry/external'
import { transform } from '@/src/utils/transformers'
import chalk from 'chalk'
import { Command } from 'commander'
import { execa } from 'execa'
import ora from 'ora'
import prompts from 'prompts'
import * as z from 'zod'
import { formatComponentName } from '@/src/utils/format-code'
import { preFlight } from '@/src/utils/preflight'
import * as ERRORS from '@/src/utils/errors'
import { getNpxCommand } from '../utils/npm-commands'

const addOptionsSchema = z.object({
  yes: z.boolean(),
  overwrite: z.boolean(),
  cwd: z.string(),
  path: z.string().optional(),
  name: z.string().optional(),
  token: z.string().optional(),
  silent: z.boolean(),
})

export const add = new Command()
  .name('add')
  .alias('i')
  .description('add a component to your project')
  .argument('id', 'the component id to add')
  .option('-n, --name <name>', 'the filename of the component to add.')
  .option(
    '-t, --token <token>',
    'unique token to authenticate for private components.'
  )
  .option('-y, --yes', 'skip confirmation prompt.', true)
  .option('-o, --overwrite', 'overwrite existing files.', false)
  .option(
    '-c, --cwd <cwd>',
    'the working directory. defaults to the current directory.',
    process.cwd()
  )
  .option('-p, --path <path>', 'the path to add the component to.')
  .option('-s, --silent', 'silence output.', false)
  .action(async (id, opts) => {
    await runAdd(id, opts)
  })

export async function runAdd(
  id: string,
  opts: z.infer<typeof addOptionsSchema>
) {
  try {
    const options = addOptionsSchema.parse({
      id,
      ...opts,
    })

    const cwd = path.resolve(options.cwd)
    const packageManager = await getPackageManager(cwd)

    const error = await preFlight(cwd)

    if (error === ERRORS.MISSING_DIR) {
      logger.error(
        `The path ${cwd} does not exist. Make sure the path is correct.`
      )
      process.exit(1)
    }

    // Check for existing components.json file.
    if (!existsSync(path.resolve(cwd, 'components.json'))) {
      logger.info(
        `Configuration is missing. Run the following command to create a ${chalk.green(
          'components.json'
        )} file.`
      )
      const initCommand = getNpxCommand(packageManager, 'v0@latest init')
      logger.info('')
      logger.info(`   ${chalk.green(initCommand)}`)
      logger.info('')
      logger.info(
        `When done, run ${chalk.green('v0 add <component-id>')} to add the component to your project.`
      )
      logger.info('')
      process.exit(1)
    }

    const config = await getConfig(cwd)
    if (!config) {
      logger.error(
        `Failed to read project configuration. Make sure your project is a Next.js project.`
      )
      process.exit(1)
    }

    // Validate component against registry.
    const [component] =
      (await getComponentsFromExternalRegistry([id], options.token)) || []

    // We assume that missing cssVariables is default to true and error if they try to use a theme with it disabled.
    if (config.tailwind.cssVariables === false && component.themeId) {
      logger.warn(
        `This component is using the ${chalk.cyan(
          component.themeId
        )} theme, which requires ${chalk.cyan('cssVariables')} to be set to ${chalk.cyan(
          'true'
        )} in your ${chalk.cyan('components.json')} file. Please update your ${chalk.cyan(
          'components.json'
        )} file and try again.`
      )
      logger.info('')
      process.exit(1)
    }

    if (!id) {
      logger.warn('No component id provided. Exiting.')
      process.exit(0)
    }

    let componentName = options.name

    if (!componentName) {
      const { name } = await prompts({
        type: 'text',
        name: 'name',
        message: `What should we name ${chalk.cyan('the component')}?`,
        initial: 'Component',
        format: value => value.trim(),
        validate: (value: string) =>
          value.length > 128
            ? `Name should be less than 128 characters.`
            : true,
      })

      componentName = name
    }

    if (!componentName) {
      logger.warn('No component name provided.')
      process.exit(0)
    }

    // Generate a kebab-case name from the component name.
    const kebabName = componentName
      .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
      .toLowerCase()
    const filename = `${kebabName}.${config.tsx ? 'tsx' : 'jsx'}`

    component.name = kebabName
    component.files[0].name = filename

    // Update the component name.
    // We can just do regex replace here since we're only updating the first export.
    component.files[0].content = formatComponentName(
      component.files[0].content,
      componentName
    )

    const registryIndex = await getRegistryIndex()

    const tree = await resolveTree(
      registryIndex,
      component.registryDependencies ?? []
    )
    const payload = await fetchTree(config.style, tree)

    const existingPrimitives: string[] = []
    for (const item of payload) {
      if (item.type !== 'components:ui') {
        continue
      }

      // Filter out existing primitives from the payload.
      const targetDir = await getItemTargetPath(
        config,
        item,
        options.path ? path.resolve(cwd, options.path) : undefined
      )

      if (!targetDir) {
        continue
      }

      const existingComponent = item.files.filter(file =>
        existsSync(path.resolve(targetDir, file.name))
      )

      if (existingComponent.length) {
        existingPrimitives.push(item.name)
      }
    }

    const filteredPayload = payload.filter(
      item => !existingPrimitives.includes(item.name)
    )

    filteredPayload.push(component)

    if (!filteredPayload.length) {
      logger.warn('Invalid component dependencies found. Exiting.')
      process.exit(0)
    }

    if (!options.yes) {
      const { proceed } = await prompts({
        type: 'confirm',
        name: 'proceed',
        message: `Ready to add components and dependencies. Proceed?`,
        initial: true,
      })

      if (!proceed) {
        process.exit(0)
      }
    }

    const baseColor = await getRegistryBaseColor(config.tailwind.baseColor)
    const imageDestinationDir = path.resolve(cwd, 'public')

    const spinner = ora(`Adding components...`)
    if (!options.silent) {
      spinner.start()
    }

    for (const item of filteredPayload) {
      spinner.text = `Adding ${
        item.type === 'components:component' ? 'component' : item.name
      }...`
      let targetDir = await getItemTargetPath(
        config,
        item,
        options.path ? path.resolve(cwd, options.path) : undefined
      )

      if (!targetDir) {
        continue
      }

      // Strip the /component suffix.
      targetDir = targetDir.replace(/\/component$/, '')

      if (!existsSync(targetDir)) {
        await fs.mkdir(targetDir, { recursive: true })
      }

      for (const file of item.files) {
        let filePath = path.resolve(targetDir, file.name)

        // Run transformers.
        const content = await transform({
          filename: file.name,
          raw: file.content,
          config,
          baseColor,
        })

        if (!config.tsx) {
          filePath = filePath.replace(/\.tsx$/, '.jsx')
        }

        await fs.writeFile(filePath, content)
      }

      // Add dependencies.
      if (item.dependencies?.length) {
        await execa(
          packageManager,
          [packageManager === 'npm' ? 'add' : 'add', ...item.dependencies],
          {
            cwd,
          }
        )
      }

      // Download images to the public directory.
      // Assumes the public directory exists.
      if (item.images?.length) {
        for (const image of item.images) {
          // Skip absolute URLs.
          if (!image.startsWith('/')) {
            continue
          }

          const imagePath = path.join(imageDestinationDir, path.basename(image))

          if (existsSync(imagePath)) {
            continue
          }

          await downloadImageFromRegistry(image, imagePath)
        }
      }
    }
    if (!options.silent) {
      if (component.themeId) {
        spinner.succeed(`Done.`)
        logger.info('')
        logger.info(`The component has been added to your project.`)
        logger.info(
          `To install theme colors and fonts, visit ${chalk.cyan(
            `https://v0.dev/t/${id}`
          )} and follow the instructions under the Code tab.`
        )
        logger.info('')
      } else {
        spinner.succeed(`Done.`)
      }
    }
  } catch (error) {
    console.log({ error })
    handleError(error)
  }
}
